"use client";

import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  Text,
  Box,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  useToast,
} from "@chakra-ui/react";
import { ChevronDownIcon } from "@chakra-ui/icons";
import { useAppTheme } from "../../../ThemeContext.tsx";
import type { PronunciationEntry } from "./PronunciationDictionary.tsx";

interface PronunciationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: PronunciationEntry) => Promise<void>;
  initialData?: PronunciationEntry | null;
}

interface CustomSelectProps {
  label: string;
  value: string;
  placeholder: string;
  options: Array<{ value: string; label: string }>;
  onSelect: (value: string) => void;
  isDisabled?: boolean;
  cardBg: string;
  cardComponentsBg: string;
  textColor: string;
  borderColor: string;
  isDark: boolean;
}

// CustomSelect component styled similarly to the one in PhoneNumberDetails
const CustomSelect: React.FC<CustomSelectProps> = ({
  label,
  value,
  placeholder,
  options,
  onSelect,
  isDisabled = false,
  cardBg,
  cardComponentsBg,
  textColor,
  borderColor,
  isDark,
}) => {
  const selectedOption = options.find((opt) => opt.value === value);
  const buttonRef = React.useRef<HTMLButtonElement>(null);

  return (
    <Box>
      <Text mb={2} fontWeight="medium" color={textColor}>
        {label}
      </Text>
      <Menu>
        <MenuButton
          as={Button}
          ref={buttonRef}
          rightIcon={<ChevronDownIcon />}
          isDisabled={isDisabled}
          bg={cardBg}
          color={textColor}
          borderColor={borderColor}
          borderWidth="1px"
          _hover={{ bg: cardBg }}
          _expanded={{ bg: cardBg }}
          w="100%"
          textAlign="left"
          fontSize={14}
        >
          {selectedOption ? selectedOption.label : placeholder}
        </MenuButton>
        <MenuList
          bg={cardBg}
          borderColor={borderColor}
          minW={
            buttonRef.current ? `${buttonRef.current.offsetWidth}px` : "100%"
          }
          fontSize={14}
        >
          <MenuItem
            onClick={() => onSelect("")}
            _hover={{ bg: isDark ? "gray.700" : "gray.300" }}
            bg={cardComponentsBg}
            color={textColor}
          >
            {placeholder}
          </MenuItem>
          {options.map((option) => (
            <MenuItem
              key={option.value}
              onClick={() => onSelect(option.value)}
              _hover={{ bg: isDark ? "gray.700" : "gray.300" }}
              bg={cardComponentsBg}
              color={textColor}
            >
              {option.label}
            </MenuItem>
          ))}
        </MenuList>
      </Menu>
    </Box>
  );
};

export const PronunciationModal: React.FC<PronunciationModalProps> = ({
  isOpen,
  onClose,
  onSave,
  initialData,
}) => {
  const [word, setWord] = useState("");
  const [alphabet, setAlphabet] = useState<"ipa" | "cmu">("cmu");
  const [phoneme, setPhoneme] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { resolvedTheme } = useAppTheme();
  const toast = useToast();
  const isDark = resolvedTheme === "dark";

  // Theme based colors
  const cardBg = isDark ? "gray.800" : "white";
  const cardComponentsBg = isDark ? "gray.700" : "white";
  const textColor = isDark ? "white" : "gray.800";
  const borderColor = isDark ? "gray.600" : "gray.300";

  useEffect(() => {
    if (initialData) {
      setWord(initialData.word);
      setAlphabet(initialData.alphabet);
      setPhoneme(initialData.phoneme);
    } else {
      setWord("");
      setAlphabet("cmu");
      setPhoneme("");
    }
  }, [initialData]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await onSave({
        word,
        alphabet,
        phoneme,
      });
      toast({
        title: "Pronunciation saved",
        status: "success",
        duration: 2000,
      });
    } catch (error: any) {
      toast({
        title: "Error saving pronunciation",
        description:
          error.response?.data?.message || "An unexpected error occurred",
        status: "error",
        duration: 3000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Options for the custom select for the "Pronunciation" alphabet
  const alphabetOptions = [
    { value: "cmu", label: "CMU" },
    { value: "ipa", label: "IPA" },
  ];

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalOverlay />
      <ModalContent bg={cardBg} textColor={textColor}>
        <form onSubmit={handleSubmit}>
          <ModalHeader color={textColor}>
            {initialData ? "Edit Pronunciation" : "Add Pronunciation"}
          </ModalHeader>
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>Word</FormLabel>
                <Input
                  id="pronunciation-word"
                  value={word}
                  onChange={(e) => setWord(e.target.value)}
                  placeholder="Enter word or phrase"
                  _placeholder={{ color: isDark ? "gray.400" : "gray.600" }}
                  bg={isDark ? "gray.700" : "white"}
                  borderColor={isDark ? "gray.600" : "gray.300"}
                  _hover={{ borderColor: isDark ? "gray.500" : "gray.400" }}
                  _focus={{
                    borderColor: isDark ? "gray.500" : "gray.400",
                    boxShadow: "none",
                  }}
                />
              </FormControl>

              <FormControl isRequired>
                <CustomSelect
                  label="Pronunciation"
                  value={alphabet}
                  options={alphabetOptions}
                  onSelect={(val) =>
                    setAlphabet((val as "ipa" | "cmu") || "cmu")
                  }
                  isDisabled={isSubmitting}
                  cardBg={cardBg}
                  cardComponentsBg={cardComponentsBg}
                  textColor={textColor}
                  borderColor={borderColor}
                  isDark={isDark}
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Phoneme</FormLabel>
                <Input
                  id="pronunciation-phoneme"
                  value={phoneme}
                  onChange={(e) => setPhoneme(e.target.value)}
                  placeholder="Enter phonetic pronunciation"
                  _placeholder={{ color: isDark ? "gray.400" : "gray.600" }}
                  bg={isDark ? "gray.700" : "white"}
                  borderColor={isDark ? "gray.600" : "gray.300"}
                  _hover={{ borderColor: isDark ? "gray.500" : "gray.400" }}
                  _focus={{
                    borderColor: isDark ? "gray.500" : "gray.400",
                    boxShadow: "none",
                  }}
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button
              variant="ghost"
              mr={3}
              onClick={onClose}
              colorScheme={isDark ? "black" : "white"}
              textColor={isDark ? "whiteAlpha" : "gray.800"}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              colorScheme="gray"
              isLoading={isSubmitting}
              _hover={{ bg: isDark ? "gray.600" : "gray.300" }}
              bg={isDark ? "gray.700" : "gray.200"}
              textColor={isDark ? "whiteAlpha" : "gray.800"}
            >
              Save
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
};
